import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './SearchModal.css';

const SearchModal = ({ isOpen, onClose, searchTerm, results, isLoading }) => {
  const [searchType, setSearchType] = useState('all'); // 'all', 'products', 'pages'
  const [filteredResults, setFilteredResults] = useState([]);

  // Filter results by type
  useEffect(() => {
    if (!results || results.length === 0) {
      setFilteredResults([]);
      return;
    }

    if (searchType === 'all') {
      setFilteredResults(results);
    } else if (searchType === 'products') {
      setFilteredResults(results.filter(r => r.type === 'product'));
    } else if (searchType === 'pages') {
      setFilteredResults(results.filter(r => r.type !== 'product'));
    }
  }, [results, searchType]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      // Reset to all results when modal opens
      setSearchType('all');
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);


  // Function to generate link based on result type
  const getResultLink = (result) => {
    switch (result.type) {
      case 'product':
        if (result.id) return `/product/${result.id}`;
        return `/products?search=${encodeURIComponent(searchTerm)}`;
      
      case 'page':
        return result.url || '/';
      
      case 'category':
        // Handle both string and object category slugs
        const categorySlug = typeof result.slug === 'string' ? result.slug : result.categorySlug || result.category;
        return `/products?category=${encodeURIComponent(categorySlug)}`;
      
      case 'brand':
        // Handle both string and object brand slugs
        const brandSlug = typeof result.slug === 'string' ? result.slug : result.brandSlug || result.brand;
        return `/products?brand=${encodeURIComponent(brandSlug)}`;
      
      case 'blog':
        return `/blog/${result.slug || result.id}`;
      
      case 'contact':
        return '/contact';
      
      case 'about':
        return '/about';
      

      case 'specialist':
        return '/specialist';
      
      default:
        return '/';
    }
  };

  // Function to render different result types
  const renderResult = (result, index) => {
    const link = getResultLink(result);
    
    switch (result.type) {
      case 'product':
        return (
          <div key={index} className="result-item product-item">
            <div className="result-type-badge">Product</div>
            <div className="result-title">{result.title}</div>
            <div className="result-meta">
              {result.brand && <span className="result-brand">{result.brand}</span>}
              {result.category && <span className="result-category">{result.category}</span>}
              {result.price && <span className="result-price">P {result.price}</span>}
            </div>
            <div className="result-description">{result.description}</div>
            <Link to={link} className="view-btn-link" onClick={onClose}>
              View Product
            </Link>
          </div>
        );
      

      case 'page':
      case 'about':
      case 'contact':
      case 'specialist':
        return (
          <div key={index} className="result-item page-item">
            <div className="result-type-badge">Page</div>
            <h4 className="result-title">{result.title}</h4>
            {result.heading && <div className="result-heading">{result.heading}</div>}
            {result.content && (
              <div className="result-excerpt">
                {result.content.substring(0, 150)}...
              </div>
            )}
            <Link to={link} className="view-btn-link" onClick={onClose}>
              View Page
            </Link>
          </div>
        );
      
      case 'category':
        return (
          <div key={index} className="result-item category-item">
            <div className="result-type-badge">Category</div>
            <h3 className="result-title">{result.title}</h3>
            {result.description && (
              <div className="result-description">{result.description}</div>
            )}
            <Link to={link} className="view-btn-link" onClick={onClose}>
              Browse Category
            </Link>
          </div>
        );
      
      case 'brand':
        return (
          <div key={index} className="result-item brand-item">
            <div className="result-type-badge">Brand</div>
            <h3 className="result-title">{result.title}</h3>
            {result.description && (
              <div className="result-description">{result.description}</div>
            )}
            <Link to={link} className="view-btn-link" onClick={onClose}>
              View Brand Products
            </Link>
          </div>
        );
      
      case 'blog':
        return (
          <div key={index} className="result-item blog-item">
            <div className="result-type-badge">Article</div>
            <h3 className="result-title">{result.title}</h3>
            {result.excerpt && (
              <div className="result-excerpt">{result.excerpt}</div>
            )}
            <div className="result-meta">
              {result.date && <span className="result-date">{result.date}</span>}
              {result.author && <span className="result-author">By {result.author}</span>}
            </div>
            <Link to={link} className="view-btn-link" onClick={onClose}>
              Read Article
            </Link>
          </div>
        );
      
      default:
        return (
          <div key={index} className="result-item">
            <div className="result-title">{result.title}</div>
            <Link to={link} className="view-btn-link" onClick={onClose}>
              View
            </Link>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="searchmodal-overlay" onClick={onClose}>
      <div className="search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="searchmodal-header">
          <div className="search-header-top">
            <h3>Search Results for "{searchTerm}"</h3>
            <button className="searchmodal-close-btn" onClick={onClose}>×</button>
          </div>
          
          {/* Search Type Filters */}
          <div className="search-type-filters">
            <button 
              className={`filter-btn ${searchType === 'all' ? 'active' : ''}`}
              onClick={() => setSearchType('all')}
            >
              All Results
            </button>
            <button 
              className={`filter-btn ${searchType === 'products' ? 'active' : ''}`}
              onClick={() => setSearchType('products')}
            >
              Products Only
            </button>
            <button 
              className={`filter-btn ${searchType === 'pages' ? 'active' : ''}`}
              onClick={() => setSearchType('pages')}
            >
              Pages & Content
            </button>
          </div>
        </div>
        
        <div className="searchmodal-content">
          {isLoading ? (
            <div className="searchmodal-loading">
              <div className="searchmodal-spinner"></div>
              <div>Searching website...</div>
            </div>
          ) : filteredResults.length > 0 ? (
            <div className="searchmodal-results-list">
              {filteredResults.map((result, index) => renderResult(result, index))}
            </div>
          ) : (
            <div className="searchmodal-no-results">
              <div className="searchmodal-no-results-icon">🔍</div>
              <h4>No results found for "{searchTerm}"</h4>
              <p>Try different keywords or browse our categories:</p>
              <div className="searchmodal-suggestions">
                <Link to="/products" onClick={onClose}>All Products</Link>
                <Link to="/products?category=solar_panels" onClick={onClose}>Solar Panels</Link>
                <Link to="/products?category=solar_inverters" onClick={onClose}>Inverters</Link>
                <Link to="/products?category=batteries" onClick={onClose}>Batteries</Link>
              </div>
            </div>
          )}
        </div>
        
        {/* Results Summary */}
        {filteredResults.length > 0 && (
          <div className="searchmodal-footer">
            <div className="searchmodal-results-summary">
              <span>Found {filteredResults.length} results</span>
              {searchType === 'all' && results.length > 0 && (
                <span className="searchmodal-type-breakdown">
                  ({results.filter(r => r.type === 'product').length} products, 
                  {results.filter(r => r.type !== 'product').length} pages)
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchModal;